import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { StatusBadge, EmptyState } from "@/components/common";
import { TextField, AreaField } from "@/components/fields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Circle } from "@phosphor-icons/react";
import { DocUploader } from "@/components/DocUploader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const Info = ({ label, value }) => (
  <div><div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div><div className="font-medium">{value || "—"}</div></div>
);
const EMPTY_AVAL = { nombre: "", telefono: "", rfc: "", notas: "", direcciones: [], documentos: [] };
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [aval, setAval] = useState(EMPTY_AVAL);

  const load = useCallback(async () => {
    const data = (await api.get(`/tenants/${id}`)).data;
    setT(data);
    const av = data.aval || {};
    let direcciones = av.direcciones || [];
    if (!direcciones.length && av.direccion) direcciones = [{ id: uid(), direccion: av.direccion, es_garantia: true }];
    setAval({ ...EMPTY_AVAL, ...av, direcciones, documentos: av.documentos || [] });
    api.get("/contracts").then((r) => setContracts(r.data.filter((c) => c.tenant_id === id)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const putTenant = async (patch) => {
    const body = { ...t, ...patch };
    await api.put(`/tenants/${id}`, body);
    setT(body);
  };

  const saveDocs = async (docs) => { try { await putTenant({ documentos: docs }); toast.success("Documentos actualizados"); } catch (e) { toast.error(errMsg(e)); } };
  const saveAval = async () => { try { await putTenant({ aval, fiador: aval.nombre || t.fiador }); toast.success("Aval guardado"); } catch (e) { toast.error(errMsg(e)); } };
  const addDireccion = () => setAval((a) => ({ ...a, direcciones: [...(a.direcciones || []), { id: uid(), direccion: "", es_garantia: (a.direcciones || []).length === 0 }] }));
  const updDireccion = (did, val) => setAval((a) => ({ ...a, direcciones: a.direcciones.map((d) => (d.id === did ? { ...d, direccion: val } : d)) }));
  const setGarantia = (did) => setAval((a) => ({ ...a, direcciones: a.direcciones.map((d) => ({ ...d, es_garantia: d.id === did })) }));
  const removeDireccion = (did) => setAval((a) => ({ ...a, direcciones: a.direcciones.filter((d) => d.id !== did) }));

  if (!t) return <div className="text-muted-foreground">Cargando…</div>;

  return (
    <div data-testid="tenant-detail-page">
      <Button variant="ghost" className="rounded-sm gap-2 mb-4 -ml-2" onClick={() => navigate("/arrendatarios")}><ArrowLeft size={16} /> Arrendatarios</Button>
      <h1 className="text-3xl font-display font-bold tracking-tight mb-1">{t.nombre}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t.telefono || "—"} · {t.email || "—"}</p>

      <Tabs defaultValue="info">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="info" data-testid="tab-info">Información</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-t-documentos">Documentos</TabsTrigger>
          <TabsTrigger value="aval" data-testid="tab-aval">Aval / Fiador</TabsTrigger>
          <TabsTrigger value="contratos" data-testid="tab-contratos">Contratos</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Info label="Teléfono" value={t.telefono} />
            <Info label="Correo" value={t.email} />
            <Info label="Identificación" value={t.identificacion} />
            <Info label="RFC" value={t.rfc} />
            <Info label="Dirección" value={t.direccion} />
            <Info label="Referencias" value={t.referencias} />
          </div>
        </TabsContent>

        <TabsContent value="documentos" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl">
            <DocUploader value={t.documentos || []} onChange={saveDocs} label="Documentos (INE, comprobante de domicilio, etc.)" testId="tenant-doc-input" />
          </div>
        </TabsContent>

        <TabsContent value="aval" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-6 max-w-2xl">
            <h3 className="font-display font-bold mb-4">Datos del aval / obligado solidario</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Nombre" value={aval.nombre} onChange={(v) => setAval((a) => ({ ...a, nombre: v }))} testId="aval-nombre" col={2} />
              <TextField label="Teléfono" value={aval.telefono} onChange={(v) => setAval((a) => ({ ...a, telefono: v }))} />
              <TextField label="RFC" value={aval.rfc} onChange={(v) => setAval((a) => ({ ...a, rfc: v }))} />
              <AreaField label="Notas" value={aval.notas} onChange={(v) => setAval((a) => ({ ...a, notas: v }))} />
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Direcciones del aval</Label>
                <Button type="button" size="sm" variant="outline" className="rounded-sm" data-testid="aval-add-direccion" onClick={addDireccion}>+ Agregar dirección</Button>
              </div>
              {(aval.direcciones || []).length === 0 && <p className="text-xs text-muted-foreground">Agrega al menos una dirección y marca cuál es la de garantía.</p>}
              <div className="space-y-2">
                {(aval.direcciones || []).map((d, i) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <button type="button" data-testid={`aval-garantia-${i}`} onClick={() => setGarantia(d.id)} className={`shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-sm border ${d.es_garantia ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-border text-muted-foreground"}`} title="Marcar como dirección de garantía">
                      {d.es_garantia ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />} Garantía
                    </button>
                    <Input value={d.direccion} data-testid={`aval-direccion-${i}`} onChange={(e) => updDireccion(d.id, e.target.value)} placeholder="Dirección" className="rounded-sm h-9" />
                    <button type="button" className="text-red-600 shrink-0" onClick={() => removeDireccion(d.id)}>✕</button>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <DocUploader value={aval.documentos || []} onChange={(docs) => setAval((a) => ({ ...a, documentos: docs }))} label="Documentos del aval" testId="aval-doc-input" />
            </div>
            <div className="mt-4 flex justify-end">
              <Button className="rounded-sm gap-2" data-testid="save-aval-btn" onClick={saveAval}><FloppyDisk size={16} /> Guardar aval</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contratos" className="mt-4">
          {contracts.length === 0 ? (
            <EmptyState title="Sin contratos" hint="Este arrendatario no tiene contratos registrados." />
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Inmueble</TableHead><TableHead className="text-right">Renta</TableHead><TableHead>Vigencia</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {contracts.map((c) => (
                    <TableRow key={c.id}><TableCell className="font-medium">{c.property_nombre}</TableCell><TableCell className="text-right tabular-nums">{money(c.renta_mensual)}</TableCell><TableCell className="text-xs">{fdate(c.fecha_inicio)} → {fdate(c.fecha_fin)}</TableCell><TableCell><StatusBadge estado={c.estado} /></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
